import { useTranslation } from 'react-i18next'
import { Box, Stack, TableBody, TableCell, Typography, useTheme } from '@mui/material'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { makeStyles } from 'tss-react/mui'
import { DetailedGapDescription, EmpiricalConcept, Variable } from '../../shared/model/models'
import { translatedDomainName } from '../../shared/utils'

type FsDetailedDescriptiveOverviewProps = {
  detailedGapDescription: DetailedGapDescription
}

interface GroupDataItem {
  referenceGroupName: string
  groupOfInterestName: string
  descriptiveName: string
  referenceGroupCount: number
  groupOfInterestCount: number
  empiricalConceptName: EmpiricalConcept
  groupVariable: Variable
}

const useStyles = makeStyles()(() => ({
  root: {
    maxWidth: 'auto',
    maxHeight: 'auto',
  },
  content: {
    display: 'flex',
    justifyContent: 'space-evenly',
  },
}))

const tableHeadStyle = { fontWeight: 'bolder', fontSize: '1.0em' }
const tableColumnStyle = { fontSize: '0.9em', width: '20%' }

const FsDetailedDescriptiveOverview = (props: FsDetailedDescriptiveOverviewProps) => {
  const { t, i18n } = useTranslation()
  const { detailedGapDescription } = props
  const { classes } = useStyles()
  const groupDataItems: GroupDataItem[] = []
  const theme = useTheme()

  detailedGapDescription.referenceGroup.descriptions.forEach((detailedDescription, index) => {
    groupDataItems.push({
      referenceGroupName: detailedGapDescription.referenceGroup.groupVariable.label,
      groupOfInterestName: detailedGapDescription.groupOfInterest.groupVariable.label,
      descriptiveName: detailedDescription.descriptiveVariable.label,
      referenceGroupCount: detailedDescription.descriptionDependentVariable.numberOfObservations,
      groupOfInterestCount:
        detailedGapDescription.groupOfInterest.descriptions[index].descriptionDependentVariable
          .numberOfObservations,
      empiricalConceptName:
        detailedDescription.descriptionDependentVariable.characteristic.empiricalConcept,
      groupVariable: detailedGapDescription.referenceGroup.groupVariable,
    })
  })

  const totalGoi = Math.round(
    groupDataItems.reduce((prev, curr) => prev + +curr.groupOfInterestCount, 0),
  ).toLocaleString(i18n.language)

  const helpTextHtml =
    detailedGapDescription &&
    detailedGapDescription.gapCauseDescriptions.filter((info) => info.locale === i18n.language)
      ? detailedGapDescription.gapCauseDescriptions.filter(
          (info) => info.locale === i18n.language,
        )[0].gapEmpiricalOverview
      : ''

  return (
    <div
      className={classes.root}
      style={{
        backgroundColor: theme.palette.background.default,
      }}
    >
      {Array.isArray(groupDataItems) && groupDataItems.length > 0 ? (
        <TableContainer>
          <Table sx={{ width: '100%', maxWidth: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadStyle} align="right">
                  {}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {}
                </TableCell>
                <TableCell sx={{ ...tableHeadStyle, paddingLeft: 10 }} align="left">
                  {t(`context.model.name.${groupDataItems[0].groupVariable.name.split('@')[0]}` as any)}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={tableHeadStyle}>
                  {t(
                    `descriptiveCharacteristic.name.${detailedGapDescription.groupOfInterest.descriptiveCharacteristic.name}` as any,
                  )}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {translatedDomainName(
                    `${groupDataItems.map((tableData) => tableData.groupOfInterestName)[0]}`,
                    t,
                  )}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {translatedDomainName(
                    `${groupDataItems.map((tableData) => tableData.referenceGroupName)[0]}`,
                    t,
                  )}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {t('total')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupDataItems.map((groupDataItem) => (
                <TableRow key={groupDataItem.descriptiveName}>
                  <TableCell
                    sx={{ ...tableColumnStyle, width: '40%', ...tableHeadStyle }}
                    component="th"
                    scope="row"
                  >
                    {translatedDomainName(`${groupDataItem.descriptiveName}`, t)
                      .charAt(0)
                      .toUpperCase() +
                      translatedDomainName(`${groupDataItem.descriptiveName}`, t).slice(1)}
                  </TableCell>
                  <TableCell sx={tableColumnStyle} align="right">
                    {Math.round(groupDataItem.groupOfInterestCount).toLocaleString(i18n.language)}
                  </TableCell>
                  <TableCell sx={tableColumnStyle} align="right">
                    {Math.round(groupDataItem.referenceGroupCount).toLocaleString(i18n.language)}
                  </TableCell>
                  <TableCell sx={tableColumnStyle} align="right">
                    {(
                      Math.round(groupDataItem.referenceGroupCount) +
                      Math.round(groupDataItem.groupOfInterestCount)
                    ).toLocaleString(i18n.language)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={tableHeadStyle}>{t('total')}</TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {totalGoi}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {Math.round(
                    groupDataItems.reduce((prev, curr) => prev + +curr.referenceGroupCount, 0),
                  ).toLocaleString(i18n.language)}
                </TableCell>
                <TableCell sx={tableHeadStyle} align="right">
                  {(
                    Math.round(
                      groupDataItems.reduce((prev, curr) => prev + +curr.groupOfInterestCount, 0),
                    ) +
                    Math.round(
                      groupDataItems.reduce((prev, curr) => prev + +curr.referenceGroupCount, 0),
                    )
                  ).toLocaleString(i18n.language)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
      <Stack>
        <Box mt={2}>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {helpTextHtml}
          </Typography>
        </Box>
      </Stack>
    </div>
  )
}

export default FsDetailedDescriptiveOverview
